"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";

import { SPACE_SECTIONS, type SpaceKey } from "@/components/space/sections";

/**
 * Learning Space shell — a persistent tab bar over the six section routes.
 *
 * Lives in the route-group layout, so it mounts once and survives lateral
 * navigation between tabs: the live counts load a single time and stay put.
 * The old hub-and-spoke dashboard (land on an overview, drill in, "back to
 * hub") is gone — every section is one chip away.
 */

// Mastery Path owns its own full-height scroll; everything else scrolls inside
// the centered reading column.
const FULL_BLEED = ["/space/learning"];

function isFullBleed(pathname: string): boolean {
  return FULL_BLEED.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function SpaceMain({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { t } = useTranslation();

  const [counts, setCounts] = useState<Partial<Record<SpaceKey, number>>>({});

  useEffect(() => {
    let cancelled = false;
    // Each count loads independently so one slow/failed endpoint never blanks
    // the other badges.
    for (const section of SPACE_SECTIONS) {
      section
        .load()
        .then((n) => {
          if (!cancelled) setCounts((prev) => ({ ...prev, [section.key]: n }));
        })
        .catch(() => {
          /* leave undefined → that tab just omits its badge */
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const fullBleed = isFullBleed(pathname);

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-[var(--border)] px-5 pt-4">
        <h1 className="mb-3 px-0.5 text-[13.5px] font-semibold uppercase tracking-[0.14em] text-[var(--muted-foreground)]">
          {t("Learning Space")}
        </h1>
        <nav
          aria-label={t("Learning Space sections")}
          className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {SPACE_SECTIONS.map((section) => {
            const active =
              pathname === section.href ||
              pathname.startsWith(`${section.href}/`);
            const Icon = section.icon;
            const count = counts[section.key];
            return (
              <Link
                key={section.key}
                href={section.href}
                aria-current={active ? "page" : undefined}
                className={`group inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[13.5px] font-medium transition-colors ${
                  active
                    ? "border-transparent bg-[var(--primary)] text-[var(--primary-foreground)] shadow-[var(--shadow-soft)]"
                    : "border-[var(--border)] bg-[var(--card)] text-[var(--muted-foreground)] hover:border-[var(--ring)] hover:text-[var(--foreground)]"
                }`}
              >
                <Icon size={14} strokeWidth={1.8} />
                <span>{t(section.title)}</span>
                {count !== undefined && (
                  <span
                    className={`ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1 py-px text-[13px] font-semibold tabular-nums ${
                      active
                        ? "bg-white/20 text-[var(--primary-foreground)]"
                        : "bg-[var(--muted)] text-[var(--muted-foreground)] group-hover:bg-[var(--accent)]"
                    }`}
                  >
                    {count.toLocaleString()}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </header>

      {fullBleed ? (
        <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="mx-auto max-w-5xl px-8 py-7 pb-12">{children}</div>
        </div>
      )}
    </div>
  );
}
